import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux';
import {
  initializeAuth,
  selectAuthLoading,
  selectUser,
  signIn as signInThunk,
  signOut as signOutThunk,
} from './authSlice';
import type { AppDispatch, RootState } from './index';
import {
  clearLocalData as clearLocalDataThunk,
  selectSync,
  syncNow as syncNowThunk,
} from './syncSlice';
import { dismissToast, selectToasts, showError, showSuccess } from './toastSlice';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export function useAuth() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectAuthLoading);

  return useMemo(
    () => ({
      user,
      isLoading,
      signIn: (email: string, password: string) =>
        dispatch(signInThunk({ email, password })).unwrap(),
      signOut: () => dispatch(signOutThunk()).unwrap(),
    }),
    [dispatch, user, isLoading],
  );
}

export function useSync() {
  const dispatch = useAppDispatch();
  const sync = useAppSelector(selectSync);

  return useMemo(
    () => ({
      ...sync,
      syncNow: () => dispatch(syncNowThunk()).unwrap(),
      clearLocalData: () => dispatch(clearLocalDataThunk()).unwrap(),
    }),
    [dispatch, sync],
  );
}

export function useToast() {
  const dispatch = useAppDispatch();

  const showErrorToast = useCallback(
    (message: string) => {
      const action = showError(message);
      dispatch(action);
      window.setTimeout(() => dispatch(dismissToast(action.payload.id)), 5000);
    },
    [dispatch],
  );

  const showSuccessToast = useCallback(
    (message: string) => {
      const action = showSuccess(message);
      dispatch(action);
      window.setTimeout(() => dispatch(dismissToast(action.payload.id)), 3000);
    },
    [dispatch],
  );

  return useMemo(
    () => ({ showError: showErrorToast, showSuccess: showSuccessToast }),
    [showErrorToast, showSuccessToast],
  );
}

export function useToasts() {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector(selectToasts);

  const handleDismiss = useCallback(
    (id: string) => {
      dispatch(dismissToast(id));
    },
    [dispatch],
  );

  return { toasts, dismissToast: handleDismiss };
}

export { initializeAuth };
